import { submittedPlanOf, type ChatTurn, type ElowenTurn, type SessionEventItem, type ToolItem } from '../../lib/transcript';
import { orderDeliveries } from '../../lib/chatPresentation';

type Segment = ElowenTurn['segments'][number];
/** A turn that is never part of an assistant run's own body. */
export type NonAssistantTurn = Exclude<ChatTurn, ElowenTurn>;

/** One block the transcript renders: a single turn that is not assistant work, or a run of assistant
 *  turns (`start` inclusive, `end` exclusive) that one agent run produced. */
type TranscriptBlock =
  | { kind: 'turn'; index: number; turn: NonAssistantTurn }
  | { kind: 'run'; start: number; end: number; lastStep?: ElowenTurn };

/** An appended call transcript has no request summary; admitted voice requests carry their summary in text.
 * This is conversation content, not a new user request or an assistant work boundary. */
export function isConversationOnlyTurn(turn: ChatTurn): boolean {
  return turn.role === 'you' && turn.voiceCall !== undefined && turn.text === '';
}

/** Group native assistant steps, joining across terminal durationMs only for a delivery-triggered turn.
 * Visible user turns, dividers and untriggered native turns open independent blocks. Events between
 * joined steps belong to their work; trailing events stay outside. workPending keeps the existing
 * last group live without another shell. A fresh send awaiting its user echo still reserves a shell. */
export function transcriptBlocks(turns: readonly ChatTurn[], working = false, awaitingEcho = false, workPending = false, conversationAppendCount = 0): TranscriptBlock[] {
  const blocks: TranscriptBlock[] = [];
  let i = 0;
  while (i < turns.length) {
    const turn = turns[i]!;
    if (turn.role !== 'elowen') { blocks.push({ kind: 'turn', index: i, turn }); i++; continue; }
    let last = i;
    let lastStep = turn;
    while (true) {
      let next = last + 1;
      while (turns[next] && (turns[next]!.role === 'event' || isConversationOnlyTurn(turns[next]!))) next++;
      const following = turns[next];
      if (following?.role !== 'elowen' || (lastStep.durationMs != null && following.trigger !== 'delivery')) break;
      last = next;
      lastStep = following;
    }
    blocks.push({ kind: 'run', start: i, end: last + 1, lastStep });
    // Inert cards between joined steps stay standalone, never hidden inside the work disclosure.
    for (let index = i + 1; index < last; index++) {
      const item = turns[index]!;
      if (item.role === 'you' && isConversationOnlyTurn(item)) blocks.push({ kind: 'turn', index, turn: item });
    }
    i = last + 1;
  }
  // A fresh send and a hydrated run awaiting its first output use the same empty run shell.
  // This is display grouping only: user messages remain authoritative daemon echoes.
  const activeEnd = turns.length - conversationAppendCount;
  const lastContent = turns.findLast((turn, index) => index < activeEnd && turn.role !== 'event' && !isConversationOnlyTurn(turn));
  if (awaitingEcho || ((working || workPending) && (lastContent?.role !== 'elowen' || (!workPending && lastContent.durationMs != null)))) {
    // A pending append is not another user request. Its card stays after the current run's shell.
    const suffix = blocks.findIndex(block => (block.kind === 'turn' ? block.index : block.start) >= activeEnd);
    blocks.splice(suffix === -1 ? blocks.length : suffix, 0, { kind: 'run', start: activeEnd, end: activeEnd });
  }
  return blocks;
}

/** One entry of a run's work timeline: a segment of an assistant step, or the event rows between steps. */
export type WorkNode =
  | { kind: 'segment'; segment: Segment }
  | { kind: 'events'; events: SessionEventItem[] };

/** A run split for display: the timeline, the items still waiting for the reader that a collapsed
 *  timeline must not hide, and the visible tail. */
interface RunParts {
  work: WorkNode[];
  waiting: Segment[];
  tail: Segment[];
}

/** A closing plan or question reads as the run's summary, so it belongs to the visible tail. */
function closesRun(item: ToolItem): boolean {
  return submittedPlanOf(item) !== undefined || item.name === 'AskUserQuestion';
}

/** How many items at the end of a tools segment close the run. */
function closingItems(items: readonly ToolItem[]): number {
  let count = 0;
  while (count < items.length && closesRun(items[items.length - 1 - count]!)) count++;
  return count;
}

/** Split one run. The visible tail is the trailing text, message blocks and closing plan or question
 *  after the run's last ordinary work step; everything before it is the timeline. A plan still awaiting
 *  its decision or a view still waiting for an answer is lifted out of the timeline, so a collapsed run
 *  never hides something the reader has to answer. The same rule applies while the run streams: the
 *  text being written after the last tool is the tail, and it moves into the timeline only when another
 *  work step follows it. Authoring a tool is already a following work step, even before the tool's
 *  arguments finish streaming and its ordinary row arrives.
 *
 *  A run that finished successfully (settled, neither stopped nor failed) often ends with a
 *  tool call after its answer, such as saving a memory. When nothing after its last work step is text, its
 *  last text segment is the answer: it stays visible ahead of that tail and the calls after it fold into
 *  the timeline. A live, stopped or failed run keeps the strict rule, and so does an `interim` turn of a
 *  joined section: its answer folds back into its own work in recorded order (`splitNativeRuns`).
 *
 *  While the joined section is live, delivered files, images, plans and completed views stay in the timeline
 *  in recorded order. Only when the whole section settles and automatically folds do deliveries precede
 *  the tail, including stopped or failed sections without final text. Read image previews stay beside
 *  their tool rows. When settled, files and images do not break the closing text; completed views remain
 *  boundaries during the tail walk so preceding narration stays in work.
 *
 *  Segments keep their identity wherever a split does not cut through them, so a settled step's memoized
 *  rows are not drawn again when a token lands in the live one. */
export function splitRun(turns: readonly ChatTurn[], pendingPlanId: string | undefined, settled: boolean, outcome?: 'stopped' | 'failed', interim = false): RunParts {
  const items: WorkNode[] = [];
  const deliveries: Segment[] = [];
  for (const turn of turns) {
    if (turn.role === 'event') { items.push({ kind: 'events', events: turn.events }); continue; }
    if (turn.role !== 'elowen') continue;
    // A plan keeps its complete tool item, including the approval answer. Split only mixed tool
    // segments so the existing delivery path can move it without moving ordinary tool rows.
    const segments = settled ? turn.segments.flatMap(segment => {
      if (segment.kind !== 'tools' || isDelivery(segment) || !segment.items.some(item => submittedPlanOf(item) !== undefined)) return [segment];
      const split: Segment[] = [];
      let start = 0;
      for (let i = 0; i < segment.items.length; i++) {
        if (submittedPlanOf(segment.items[i]!) === undefined) continue;
        if (i > start) split.push({ kind: 'tools', items: segment.items.slice(start, i) });
        split.push({ kind: 'tools', items: [segment.items[i]!] });
        start = i + 1;
      }
      if (start < segment.items.length) split.push({ kind: 'tools', items: segment.items.slice(start) });
      return split;
    }) : turn.segments;
    for (const segment of segments) {
      if (segment.kind === 'text' && !segment.text.trim()) continue;
      if (segment.kind === 'tools' && segment.items.length === 0) continue;
      if (settled && isDelivery(segment)) {
        deliveries.push(segment);
        // Keep views as boundaries until the closing-answer walk has finished.
        if (segment.kind !== 'view') continue;
      }
      items.push({ kind: 'segment', segment });
    }
  }

  const tail: Segment[] = [];
  const lastStep = turns.findLast(turn => turn.role === 'elowen');
  // The authoring hint follows the timeline. Do not lift its preceding text below that hint.
  const authoring = lastStep?.streaming && lastStep.composing;
  let end = items.length;
  while (end > 0 && !authoring) {
    const entry = items[end - 1]!;
    if (entry.kind === 'events') break;
    const segment = entry.segment;
    if (segment.kind === 'text' || segment.kind === 'blocks') { tail.unshift(segment); end--; continue; }
    if (segment.kind !== 'tools') break;
    const closing = closingItems(segment.items);
    if (closing === segment.items.length) { tail.unshift(segment); end--; continue; }
    if (closing > 0) {
      tail.unshift({ kind: 'tools', items: segment.items.slice(-closing) });
      items[end - 1] = { kind: 'segment', segment: { kind: 'tools', items: segment.items.slice(0, -closing) } };
    }
    break;
  }

  const work: WorkNode[] = [];
  const waiting: Segment[] = [];
  for (const entry of items.slice(0, end)) {
    if (entry.kind === 'events') { work.push(entry); continue; }
    const segment = entry.segment;
    if (segment.kind === 'view' && segment.waiting) {
      waiting.push(segment);
      continue;
    }
    if (settled && segment.kind === 'view') continue;
    const at = segment.kind === 'tools' && pendingPlanId !== undefined
      ? segment.items.findIndex(item => item.id === pendingPlanId && submittedPlanOf(item) !== undefined)
      : -1;
    if (segment.kind !== 'tools' || at < 0) { work.push({ kind: 'segment', segment }); continue; }
    const before = segment.items.slice(0, at);
    const after = segment.items.slice(at + 1);
    if (before.length) work.push({ kind: 'segment', segment: { kind: 'tools', items: before } });
    waiting.push({ kind: 'tools', items: [segment.items[at]!] });
    if (after.length) work.push({ kind: 'segment', segment: { kind: 'tools', items: after } });
  }
  if (settled && outcome === undefined && !interim && !tail.some(segment => segment.kind === 'text')) {
    const at = work.findLastIndex(node => node.kind === 'segment' && node.segment.kind === 'text');
    const answer = work[at];
    if (answer?.kind === 'segment') {
      work.splice(at, 1);
      tail.unshift(answer.segment);
    }
  }
  return { work, waiting, tail: [...orderDeliveries(deliveries), ...tail] };
}

/** A delivery already lifted out of a settled section, preserved beside its native turn's work. */
function isDelivery(segment: Segment): boolean {
  return segment.kind === 'file'
    || (segment.kind === 'image' && !segment.preview)
    || (segment.kind === 'view' && !segment.waiting)
    || (segment.kind === 'tools' && segment.items.length > 0 && segment.items.every(item => submittedPlanOf(item) !== undefined));
}

/** Split every native turn separately. Only the last native turn's answer stays outside the shared fold:
 * an earlier turn's interim answer stays in its own work at its recorded place, so the fold reads in
 * time order even when tool calls followed that answer.
 * Every native turn uses the joined section's live state: a terminal step cannot pin its deliveries
 * while conversation work is outstanding. Waiting items stay outside the fold throughout. Event rows
 * after a terminal step belong to the next native turn's work in transcript order. */
export function splitNativeRuns(turns: readonly ChatTurn[], pendingPlanId: string | undefined, live: boolean, outcome?: 'stopped' | 'failed'): RunParts[] {
  const parts = splitEachNativeTurn(turns, pendingPlanId, live, outcome);
  return parts.map((part, i) => i === parts.length - 1 ? part : {
    work: [...part.work, ...part.tail.filter(segment => live || !isDelivery(segment)).map(segment => ({ kind: 'segment' as const, segment }))],
    waiting: part.waiting,
    tail: live ? [] : part.tail.filter(isDelivery),
  });
}

function splitEachNativeTurn(turns: readonly ChatTurn[], pendingPlanId: string | undefined, live: boolean, outcome?: 'stopped' | 'failed'): RunParts[] {
  const slices: { turns: readonly ChatTurn[]; outcome?: 'stopped' | 'failed' }[] = [];
  let start = 0;
  for (let i = 0; i < turns.length; i++) {
    const turn = turns[i]!;
    if (turn.role !== 'elowen' || turn.durationMs == null) continue;
    slices.push({ turns: turns.slice(start, i + 1), outcome: i + 1 < turns.length ? runOutcome(turns[i + 1]) : outcome });
    start = i + 1;
  }
  if (start < turns.length) slices.push({ turns: turns.slice(start), outcome });
  return slices.map((slice, i) => splitRun(slice.turns, pendingPlanId, !live, slice.outcome, i < slices.length - 1));
}

/** How a finished run ended, read from the event rows directly after it. */
export function runOutcome(next: ChatTurn | undefined): 'stopped' | 'failed' | undefined {
  if (next?.role !== 'event') return undefined;
  if (next.events.some(e => e.event.details.kind === 'interrupted')) return 'stopped';
  if (next.events.some(e => e.event.details.kind === 'error')) return 'failed';
  return undefined;
}
